import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { CheckCircle2, Keyboard, ScanLine, X } from 'lucide-react';
import { toast } from 'sonner';
import { devices } from '../../lib/api';
import { enqueue, isNetworkError } from '../../lib/offlineQueue';
import { today } from '../../lib/dates';

// Bait-station / device scanning for the tech app.
//
// Labels printed from the office carry "XPDEV:<device id>"; stations that
// already have their own barcode or number are matched by label. An unknown
// code can be added as a new station on the spot. Everything works offline —
// inspections and new stations go to the outbox.
export const DEVICE_QR_PREFIX = 'XPDEV:';
const ACTIVITY = ['None', 'Low', 'Moderate', 'High'];
const ACTIONS = ['None', 'Rebaited', 'Cleaned', 'Replaced', 'Relocated'];
const CONDITIONS = ['Good', 'Needs attention', 'Damaged', 'Missing'];
const TYPES = ['Exterior Bait Station', 'Interior Rodent Trap', 'Insect Monitor', 'Fly Light', 'Termite Station', 'Other'];

const cacheKey = (customerId) => `xp_devices_${customerId}`;

function Camera({ onCode, onClose }) {
  const videoRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let controls;
    let stopped = false;
    import('@zxing/browser').then(({ BrowserMultiFormatReader }) => {
      if (stopped) return;
      const reader = new BrowserMultiFormatReader();
      reader.decodeFromConstraints({ video: { facingMode: 'environment' } }, videoRef.current, (result, _err, c) => {
        if (result && !stopped) {
          stopped = true;
          c.stop();
          if (navigator.vibrate) navigator.vibrate(80);
          onCode(result.getText());
        }
      }).then((c) => { controls = c; if (stopped) c.stop(); })
        .catch(() => setError('Camera not available — allow camera access, or type the code instead.'));
    }).catch(() => setError('Scanner could not load — type the code instead.'));
    return () => { stopped = true; if (controls) controls.stop(); };
  }, [onCode]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="station-camera">
      <div className="flex items-center justify-between p-3 text-white">
        <span className="font-semibold text-sm">Point at the station’s code</span>
        <button onClick={onClose} aria-label="Close scanner" className="p-2"><X className="w-6 h-6" /></button>
      </div>
      <div className="relative flex-1">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
        <div className="absolute inset-x-10 top-1/3 h-40 border-2 border-[#8BC53F] rounded-xl" />
      </div>
      {error && <p className="p-4 text-center text-sm text-amber-300">{error}</p>}
    </div>
  );
}

export default function StationScanner({ customerId, customerName, techName, jobId }) {
  const [list, setList] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cacheKey(customerId)) || '[]'); } catch { return []; }
  });
  const [done, setDone] = useState({});
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState('');
  const [current, setCurrent] = useState(null); // { device } or { newCode }
  const [form, setForm] = useState({ activity: 'None', action: 'None', condition: 'Good', notes: '', type: TYPES[0], location: '' });

  const load = useCallback(() => {
    if (!customerId) return;
    devices.list(customerId).then((rows) => {
      setList(rows || []);
      try { localStorage.setItem(cacheKey(customerId), JSON.stringify(rows || [])); } catch { /* full */ }
    }).catch(() => { /* offline: cached list stays */ });
    devices.inspections({ customerId }).then((rows) => {
      const t = today();
      setDone((prev) => ({ ...prev, ...Object.fromEntries((rows || []).filter((r) => String(r.date || '').slice(0, 10) === t).map((r) => [r.deviceId, true])) }));
    }).catch(() => {});
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const resolve = useCallback((raw) => {
    setScanning(false);
    const code = String(raw || '').trim();
    if (!code) return;
    const id = code.toUpperCase().startsWith(DEVICE_QR_PREFIX) ? code.slice(DEVICE_QR_PREFIX.length) : null;
    const device = list.find((d) => (id && d.id === id) || String(d.label || '').toLowerCase() === code.toLowerCase());
    if (device) setCurrent({ device });
    else if (id) toast.error('That station belongs to another customer or was removed.');
    else setCurrent({ newCode: code });
    setForm({ activity: 'None', action: 'None', condition: 'Good', notes: '', type: TYPES[0], location: '' });
  }, [list]);

  const save = async () => {
    let device = current.device;
    let queued = false;
    if (!device) {
      const body = { customerId, customerName, label: current.newCode, type: form.type, location: form.location, status: 'Active', installDate: today() };
      try {
        device = await devices.create(body);
      } catch (err) {
        if (!isNetworkError(err)) { toast.error(err?.response?.data?.detail || 'Could not add the station'); return; }
        const tempId = `offline-${Date.now()}`;
        await enqueue('device', jobId, { ...body, tempId });
        device = { ...body, id: tempId };
        queued = true;
      }
      setList((l) => [...l, device]);
    }
    const inspection = {
      deviceId: device.id, customerId, date: today(), activity: form.activity, action: form.action,
      condition: form.condition, techName, notes: form.notes, findings: form.notes,
    };
    const queueIt = async () => {
      await enqueue('inspection', jobId, inspection);
      toast.success(`${device.label} saved on the phone — it will sync when you have signal`);
    };
    if (queued) {
      await queueIt(); // the station itself is still waiting to be created
    } else {
      try {
        await devices.inspect(inspection);
        toast.success(`${device.label} inspected`);
      } catch (err) {
        if (!isNetworkError(err)) { toast.error(err?.response?.data?.detail || 'Could not save the inspection'); return; }
        await queueIt();
      }
    }
    setDone((d) => ({ ...d, [device.id]: true }));
    setCurrent(null);
  };

  const pick = (key, options) => (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o} onClick={() => setForm({ ...form, [key]: o })}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form[key] === o ? 'bg-[#8BC53F] text-white border-[#8BC53F]' : 'bg-white text-slate-700 border-slate-300'}`}>{o}</button>
      ))}
    </div>
  );

  const total = list.filter((d) => d.status !== 'Removed').length;
  const inspected = list.filter((d) => done[d.id]).length;

  return (
    <div className="space-y-3" data-testid="station-scanner">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">{total ? `${inspected} of ${total} stations checked today` : 'No stations on file for this customer yet'}</span>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <Button onClick={() => setScanning(true)} className="h-12 gap-2 bg-[#1F2937] hover:bg-black" data-testid="station-scan-btn"><ScanLine className="w-5 h-5" />Scan station</Button>
        <div className="flex gap-1">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="or type #" className="h-12 w-24" />
          <Button variant="outline" className="h-12 px-3" onClick={() => { resolve(manual); setManual(''); }} aria-label="Find station by number"><Keyboard className="w-4 h-4" /></Button>
        </div>
      </div>
      {total > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {list.filter((d) => d.status !== 'Removed').map((d) => (
            <button key={d.id} onClick={() => resolve(d.label)}>
              <Badge className={`border-0 ${done[d.id] ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                {done[d.id] && <CheckCircle2 className="w-3 h-3 mr-1" />}{d.label}
              </Badge>
            </button>
          ))}
        </div>
      )}

      {current && (
        <div className="rounded-lg border border-[#8BC53F] bg-emerald-50/40 p-3 space-y-3" data-testid="station-inspect">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-bold text-slate-900">{current.device ? current.device.label : `New station ${current.newCode}`}</div>
              <div className="text-xs text-slate-600">{current.device ? `${current.device.type || ''}${current.device.location ? ` · ${current.device.location}` : ''}` : 'Not on file — it will be added to this customer'}</div>
            </div>
            <button onClick={() => setCurrent(null)} aria-label="Cancel" className="text-slate-400"><X className="w-4 h-4" /></button>
          </div>
          {!current.device && (
            <>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm bg-white">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <Input placeholder="Where is it? (e.g. NE corner by gate)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </>
          )}
          <div><div className="text-xs font-semibold text-slate-700 mb-1">Activity</div>{pick('activity', ACTIVITY)}</div>
          <div><div className="text-xs font-semibold text-slate-700 mb-1">Action taken</div>{pick('action', ACTIONS)}</div>
          <div><div className="text-xs font-semibold text-slate-700 mb-1">Condition</div>{pick('condition', CONDITIONS)}</div>
          <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <Button onClick={save} className="w-full bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="station-save">Save inspection</Button>
        </div>
      )}

      {scanning && <Camera onCode={resolve} onClose={() => setScanning(false)} />}
    </div>
  );
}
